// École Nid Douillet - About Page (Dynamic CMS Content)

import React, { useState, useEffect } from 'react';
import { CMSContent } from '../../services/cms.api';
import { cmsApi } from '../../services/cms.api';

const AboutPage: React.FC = () => {
  const [content, setContent] = useState<CMSContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAboutContent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Load about page content from CMS
        const response = await cmsApi.getContentBySlug('a-propos', 'fr');
        if (response.data) {
          setContent(response.data);
        }
      } catch (err) {
        console.error('Error loading about content:', err);
        setError('Erreur lors du chargement du contenu. Veuillez réessayer plus tard.');
      } finally {
        setLoading(false);
      }
    };

    loadAboutContent();
  }, []);

  // Update page title and meta description
  useEffect(() => {
    if (content) {
      document.title = content.meta_title || content.title || 'À Propos - École Nid Douillet';
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription && content.meta_description) {
        metaDescription.setAttribute('content', content.meta_description);
      }
    }
  }, [content]);

  if (loading) {
    return (
      <div className="container-wide py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Chargement du contenu...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-wide py-16">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <h2 className="text-lg font-semibold text-red-800 mb-2">Erreur</h2>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="container-wide py-16">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-neutral-900 mb-6">
            À propos de l'École Nid Douillet
          </h1>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md mx-auto">
            <p className="text-yellow-800">Contenu non disponible pour le moment.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-wide py-16">
      {/* Page Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-neutral-900 mb-6">
          {content.title}
        </h1>
        {content.excerpt && (
          <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
            {content.excerpt}
          </p>
        )}
      </div>
      
      {/* Dynamic Content */}
      <div className="bg-white rounded-xl shadow-soft p-8">
        <div 
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: content.content }}
        />
      </div>
      
      {/* Contact CTA */}
      <div className="mt-12 text-center">
        <div className="bg-primary-50 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-primary-900 mb-4">
            Intéressé par notre école ?
          </h3>
          <p className="text-primary-700 mb-6">
            Contactez-nous pour planifier une visite ou obtenir plus d'informations.
          </p>
          <a 
            href="/contact" 
            className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
          >
            Nous contacter
          </a>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
